import { Sort } from '@angular/material/sort';
import {
  Cadence,
  ClientGQLDisplay,
  CollectionsSequence,
  NextStatement,
  SendStatementPayload,
  SmartStatementActionDefinitionJson,
  SmartStatementBucketSetting,
  StatementStatus,
  UpdateCollectionsSettingsPayload,
} from '@anduin/api';
import { CollectionsSettingsStateModel, SortOption } from './common-collections.model';

//#region Recipients

export class GetCollectionsRecipients {
  static readonly type: string = '[CollectionsState] GetCollectionsRecipients';

  constructor(
    public page = 1,
    public partner = '',
    public sortOption?: SortOption,
  ) {}
}

export class SearchCollectionsRecipients {
  static readonly type: string = '[CollectionsState] SearchCollectionsRecipients';

  constructor(
    public term: string,
    public partnerName?: string,
    public sort?: SortOption,
  ) {}
}

export class GetOutstandingSummaryByRecipient {
  static readonly type: string = '[CollectionsState] GetOutstandingSummaryByRecipient';

  constructor(public clientId: number) {}
}

export class UpdateRecipientListStatus {
  static readonly type: string = '[CollectionsState] UpdateRecipientListStatus';

  constructor(
    public readonly recipientId: number,
    public readonly status: StatementStatus,
  ) {}
}

export class SetSelectedStatementRecipientId {
  static readonly type: string = '[CommonCollectionsState] SetSelectedStatementRecipientId';

  constructor(public recipientId: number) {}
}

// Wasn't 100% sure what to name this. Just need to differentiate between settings related data and collections page data.
export class ClearCollectionsPageData {
  static readonly type: string = '[CollectionsState] ClearCollectionsPageData';
}

//#endregion

//#region Client Cadences and Sequences

export class GetClientCadences {
  static readonly type: string = '[CollectionsSettings] GetClientCadences';
}

export class GetActiveCadences {
  static readonly type: string = '[CollectionsSettings] GetActiveCadences';
}

export class GetSequenceAggregatesByCadenceId {
  static readonly type: string = '[CollectionsSettings] GetSequenceAggregatesByCadenceId';

  constructor(public cadenceId: number) {}
}

export class GetSequenceBySequenceId {
  static readonly type: string = '[CollectionsState] GetTriggersBySequenceId';

  constructor(public sequenceId: number) {}
}

export class UpdateCadence {
  static readonly type: string = '[CollectionsSettings] UpdateCadence';

  constructor(public cadence: Cadence) {}
}

export class UpdateSequence {
  static readonly type: string = '[CollectionsSettings] UpdateSequence';

  constructor(public sequence: CollectionsSequence) {}
}

//#endregion

//#region Statements

export class GetRecipientPreviousStatements {
  static readonly type: string = '[CollectionsState] GetRecipientPreviousStatements';

  constructor(
    public recipientId: number,
    public forceRefresh = false,
  ) {}
}

export class GetClientNextStatement {
  static readonly type: string = '[CollectionsState] GetClientNextStatement';

  constructor(public clientId: number) {}
}

// ApproveStatement

class ApproveStatement {
  static readonly type: string = '[CommonCollectionsState] ApproveStatement';

  constructor(
    public recipientId: number,
    public action: NextStatement | SendStatementPayload,
  ) {}
}

export class Clients_ApproveStatement extends ApproveStatement {
  static readonly type: string = '[CommonCollectionsState] Clients_ApproveStatement';
}

export class Collections_ApproveStatement extends ApproveStatement {
  static readonly type: string = '[CommonCollectionsState] Collections_ApproveStatement';
}

export class ApproveStatementComplete {
  static readonly type: string = '[CommonCollectionsState] ApproveStatementComplete';

  constructor(public readonly recipientId: number) {}
}

// SkipStatement

class SkipStatement {
  static readonly type: string = '[CommonCollectionsState] SkipStatement';

  constructor(
    public recipientId: number,
    public actionId: number,
  ) {}
}

export class Clients_SkipStatement extends SkipStatement {
  static readonly type: string = '[CommonCollectionsState] Clients_SkipStatement';
}

export class Collections_SkipStatement extends SkipStatement {
  static readonly type: string = '[CommonCollectionsState] Collections_SkipStatement';
}

export class SkipStatementComplete {
  static readonly type: string = '[CommonCollectionsState] SkipStatementComplete';

  constructor(public recipientId: number) {}
}

// UndoSkipStatement

export class UndoSkipStatement {
  static readonly type: string = '[CommonCollectionsState] UndoSkipStatement';

  constructor(
    public actionId: number,
    public recipientId: number,
  ) {}
}

export class Clients_UndoSkipStatement extends UndoSkipStatement {
  static readonly type: string = '[CommonCollectionsState] Clients_UndoSkipStatement';
}

export class Collections_UndoSkipStatement extends UndoSkipStatement {
  static readonly type: string = '[CommonCollectionsState] Collections_UndoSkipStatement';
}

export class UndoSkipStatementComplete {
  static readonly type: string = '[CommonCollectionsState] UndoSkipStatementComplete';

  constructor(public recipientId: number) {}
}

//#endregion

//#region Excluded Recipients

export class GetExcludedStatementRecipients {
  static readonly type: string = '[CollectionsState] GetExcludedStatementRecipients';

  constructor(
    public page = 1,
    public sort?: Sort,
  ) {}
}

export class SearchExcludedStatementRecipients {
  static readonly type: string = '[CollectionsState] SearchExcludedStatementRecipients';

  constructor(public term: string) {}
}

export class GetAlreadyExcludedCollectionsClientsTotal {
  static readonly type: string = '[CollectionsState] GetAlreadyExcludedCollectionsClientsTotal';
}

export class SortExcludedStatementRecipients {
  static readonly type: string = '[CollectionsState] SortExcludedStatementRecipients';

  constructor(public sort: Sort) {}
}

export class UpdateCollectionsClientExclusionStatus {
  static readonly type: string = '[CollectionsSettings] UpdateCollectionsClientExclusionStatus';

  constructor(public clientsToUpdate: ClientGQLDisplay[]) {}
}

export class ClearExcludedClientsState {
  static readonly type: string = '[CollectionsSettings] ClearExcludedClientsState';
}

// UpdateClientExclusion

export class UpdateClientExclusion {
  static readonly type: string = '[CommonCollectionsState] UpdateClientExclusion';

  constructor(
    public clientId: number,
    public excluded: boolean,
  ) {}
}

export class Clients_UpdateClientExclusion extends UpdateClientExclusion {
  static readonly type: string = '[CommonCollectionsState] Clients_UpdateClientExclusion';
}

export class Collections_UpdateRecipientExclusion extends UpdateClientExclusion {
  static readonly type: string = '[CommonCollectionsState] Collections_UpdateClientExclusion';
}

export class ToggleSelectedRecipientEnabled {
  static readonly type: string = '[CommonCollectionsState] ToggleSelectedRecipientEnabled';
}

//#endregion

//#region Message Settings

export class GetMessageSettings {
  static readonly type: string = '[CollectionsSettings] GetMessageSettings';

  constructor(public cadenceId: number) {}
}

export class UpdateMessageSettings {
  static readonly type: string = '[CollectionsSettings] UpdateMessageSettings';

  constructor(
    public messageSettings: SmartStatementActionDefinitionJson,
    public bucketSettings: SmartStatementBucketSetting[],
  ) {}
}

export class GetMessageVariables {
  static readonly type: string = '[CollectionsSettings] GetMessageVariables';
}

//#endregion

//#region Global Misc. Data

export class GetStaff {
  static readonly type: string = '[CollectionsState] GetStaff';

  constructor(public readonly forceRefresh = false) {}
}

//#endregion

//#region Settings v2

export type UpdatableSettingsField = keyof Omit<CollectionsSettingsStateModel, 'updateStatus'>;
const ALL_SETTINGS_FIELDS: UpdatableSettingsField[] = ['throttling', 'triggers', 'excludedRecipientIds', 'autoApprove', 'email'];

export class GetCollectionsSettings {
  static readonly type: string = '[CollectionsState] GetCollectionsSettings';

  constructor(
    public readonly forceRefresh = false,
    public readonly fields: UpdatableSettingsField[] = ALL_SETTINGS_FIELDS,
  ) {}
}

export class GetThrottlingSettings {
  static readonly type: string = '[CollectionsState] GetThrottlingSettings';

  constructor(public readonly forceRefresh = false) {}
}

export class GetTriggerSettings {
  static readonly type: string = '[CollectionsState] GetTriggerSettings';

  constructor(public readonly forceRefresh = false) {}
}

export class GetExcludedStatementRecipientIds {
  static readonly type: string = '[CollectionsState] GetExcludedStatementRecipientIds';

  constructor(public readonly forceRefresh = false) {}
}

export class GetAutoApproveSetting {
  static readonly type: string = '[CollectionsState] GetAutoApproveSetting';

  constructor(public readonly forceRefresh = false) {}
}

export class GetEmailSettings {
  static readonly type: string = '[CollectionsState] GetEmailSettings';

  constructor(public readonly forceRefresh = false) {}
}

export class UpdateCollectionsSettings {
  static readonly type: string = '[CollectionsSettings] UpdateCollectionsSettings';

  constructor(public readonly payload: UpdateCollectionsSettingsPayload) {}
}

//#endregion
